import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trophy, Eye, Clock } from "lucide-react";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate, statusColor } from "@/lib/format";

const EMPTY = { name: "", team_a: "", team_b: "", date: "", time: "", closing_time: "", status: "upcoming", reward_percentage: 10 };

export default function AdminEvents() {
  const { t } = useI18n();
  const [events, setEvents] = useState(null);
  const [form, setForm] = useState(null); // null=closed, object=open
  const [editId, setEditId] = useState(null);
  const [viewPreds, setViewPreds] = useState(null);
  const [preds, setPreds] = useState([]);
  const [publishFor, setPublishFor] = useState(null);
  const [winner, setWinner] = useState("");
  const [postponeFor, setPostponeFor] = useState(null);
  const [postponeUntil, setPostponeUntil] = useState("");

  const load = () => api.get("/admin/events").then(({ data }) => setEvents(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!events) return <Loader />;

  const save = async () => {
    try {
      if (editId) await api.put(`/admin/events/${editId}`, form);
      else await api.post("/admin/events", form);
      toast.success("Event saved"); setForm(null); setEditId(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const openView = async (ev) => {
    setViewPreds(ev);
    const { data } = await api.get(`/admin/events/${ev.id}/predictions`); setPreds(data);
  };
  const publish = async () => {
    if (!winner) { toast.error("Select a result"); return; }
    try {
      const { data } = await api.post(`/admin/events/${publishFor.id}/publish?winning_team=${encodeURIComponent(winner)}`);
      toast.success(`Result published (${data.result}). ${data.settled} predictions settled.`);
      setPublishFor(null); setWinner(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const postpone = async () => {
    if (!postponeUntil.trim()) { toast.error("Enter the new date/time or period"); return; }
    try {
      await api.put(`/admin/events/${postponeFor.id}/postpone?postponed_until=${encodeURIComponent(postponeUntil.trim())}`);
      toast.success("Event postponed. Member predictions kept safe.");
      setPostponeFor(null); setPostponeUntil(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHeader title={t("events")} subtitle="Create, control and settle events."
        action={<Button onClick={() => { setForm({ ...EMPTY }); setEditId(null); }} data-testid="create-event-button" className="bg-orange-600 hover:bg-orange-500 btn-glow font-semibold"><Plus className="w-4 h-4 me-1" />{t("create_event")}</Button>} />

      {events.length === 0 ? <Empty label="No events. Create one!" /> : (
        <div className="space-y-4">
          {events.map((ev) => (
            <div key={ev.id} className="ec-card p-5" data-testid={`admin-event-${ev.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3"><h3 className="font-head font-semibold text-white">{ev.name}</h3><Badge className={statusColor(ev.status)}>{ev.status}</Badge></div>
                  <p className="text-sm text-gray-400 mt-1">{ev.team_a} vs {ev.team_b} · {ev.reward_percentage}% · {fmtDate(ev.date + "T" + (ev.time || "00:00"))}</p>
                  {ev.final_result && <p className="text-xs text-emerald-400 mt-1">{ev.final_result === "Draw" ? "Result" : t("winner")}: {ev.final_result}</p>}
                  {ev.status === "postponed" && <p className="text-xs text-amber-400 mt-1" data-testid={`admin-postponed-${ev.id}`}>Match Postponed · New: {ev.postponed_until || "TBD"}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Select value={ev.status} onValueChange={async (v) => { await api.put(`/admin/events/${ev.id}`, { ...ev, status: v }); toast.success("Status updated"); load(); }}>
                    <SelectTrigger className="w-32 bg-white/5 border-white/10" data-testid={`event-status-${ev.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["upcoming", "open", "live", "postponed", "completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => { setForm({ ...ev }); setEditId(ev.id); }} data-testid={`edit-event-${ev.id}`} className="bg-white/5 border border-white/10 hover:bg-white/10">{t("edit")}</Button>
                  <Button size="sm" onClick={() => openView(ev)} data-testid={`view-preds-${ev.id}`} className="bg-white/5 border border-white/10 hover:bg-white/10"><Eye className="w-4 h-4 me-1" />{t("view_predictions")}</Button>
                  <Button size="sm" onClick={() => { setPostponeFor(ev); setPostponeUntil(ev.postponed_until || ""); }} disabled={ev.status === "completed"} data-testid={`postpone-${ev.id}`} className="bg-amber-600 hover:bg-amber-500 font-semibold"><Clock className="w-4 h-4 me-1" />Postpone</Button>
                  <Button size="sm" onClick={() => { setPublishFor(ev); setWinner(""); }} disabled={ev.status === "completed"} data-testid={`publish-${ev.id}`} className="bg-emerald-600 hover:bg-emerald-500 font-semibold"><Trophy className="w-4 h-4 me-1" />{t("publish_result")}</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* create/edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="ec-card border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editId ? t("edit") : t("create_event")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label>Event Name</Label><Input data-testid="event-name-input" value={form.name} onChange={set("name")} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("team_a")}</Label><Input data-testid="event-teama-input" value={form.team_a} onChange={set("team_a")} className="mt-1" /></div>
                <div><Label>{t("team_b")}</Label><Input data-testid="event-teamb-input" value={form.team_b} onChange={set("team_b")} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input data-testid="event-date-input" type="date" value={form.date} onChange={set("date")} className="mt-1" /></div>
                <div><Label>{t("time")}</Label><Input data-testid="event-time-input" type="time" value={form.time} onChange={set("time")} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("closing_time")}</Label><Input data-testid="event-closing-input" type="time" value={form.closing_time} onChange={set("closing_time")} className="mt-1" /></div>
                <div><Label>{t("reward_percentage")}</Label><Input data-testid="event-reward-input" type="number" value={form.reward_percentage} onChange={(e) => setForm({ ...form, reward_percentage: Number(e.target.value) })} className="mt-1" /></div>
              </div>
              <div>
                <Label>{t("status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="event-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["upcoming", "open", "live", "postponed", "completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={save} data-testid="save-event-button" className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* view predictions */}
      <Dialog open={!!viewPreds} onOpenChange={(o) => !o && setViewPreds(null)}>
        <DialogContent className="ec-card border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{viewPreds?.name} — {t("predictions")}</DialogTitle></DialogHeader>
          {preds.length === 0 ? <p className="text-gray-500 text-sm py-6 text-center">No predictions yet.</p> : (
            <div className="divide-y divide-white/5">
              {preds.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-3 text-sm" data-testid={`admin-pred-${p.prediction_id}`}>
                  <div><p className="text-white">{p.user_name}</p><p className="text-xs text-gray-500 keep-ltr">{p.prediction_id}</p></div>
                  <div className="text-end"><p className="text-orange-400 font-semibold">{p.selected_team}</p><p className="text-xs text-gray-400">{fmtPKR(p.amount)}</p></div>
                  <Badge className={statusColor(p.status)}>{p.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* publish result */}
      <Dialog open={!!publishFor} onOpenChange={(o) => !o && setPublishFor(null)}>
        <DialogContent className="ec-card border-white/10">
          <DialogHeader><DialogTitle className="font-head">{t("publish_result")} — {publishFor?.name}</DialogTitle></DialogHeader>
          {publishFor && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Select the result. On a team win, rewards auto-calculate and credit. On a Draw, every stake is returned with no reward.</p>
              <div className="grid grid-cols-2 gap-3">
                {[publishFor.team_a, publishFor.team_b].map((team) => (
                  <button key={team} onClick={() => setWinner(team)} data-testid={`winner-${team}`}
                    className={`rounded-xl border p-3 font-semibold ${winner === team ? "border-emerald-500 bg-emerald-500/15 text-emerald-400" : "border-white/10 bg-white/5 text-gray-200"}`}>{team}</button>
                ))}
              </div>
              <button onClick={() => setWinner("DRAW")} data-testid="winner-DRAW"
                className={`w-full rounded-xl border p-3 font-semibold ${winner === "DRAW" ? "border-amber-500 bg-amber-500/15 text-amber-400" : "border-white/10 bg-white/5 text-gray-200"}`}>Draw — return all stakes</button>
              <Button onClick={publish} data-testid="confirm-publish-button" className="w-full bg-emerald-600 hover:bg-emerald-500 font-semibold">{t("publish_result")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* postpone */}
      <Dialog open={!!postponeFor} onOpenChange={(o) => !o && setPostponeFor(null)}>
        <DialogContent className="ec-card border-white/10">
          <DialogHeader><DialogTitle className="font-head">Postpone — {postponeFor?.name}</DialogTitle></DialogHeader>
          {postponeFor && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Enter the new date/time or period. Members' existing predictions stay attached and untouched — nothing is deducted, returned or rewarded until you publish the final result.</p>
              <div>
                <Label>New date / time / period</Label>
                <Input data-testid="postpone-until-input" value={postponeUntil} onChange={(e) => setPostponeUntil(e.target.value)} className="mt-1" placeholder="e.g. 10 July 2026, 6:00 PM or 'Next Sunday'" />
              </div>
              <Button onClick={postpone} data-testid="confirm-postpone-button" className="w-full bg-amber-600 hover:bg-amber-500 font-semibold">Mark as Postponed</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
